import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  getConfigAmbiente,
  getConfigAmbienteDominio,
  showNotification
} from '../../../services/authentication/auth';
import { initEcho, getEcho } from '../../../services/authentication/echo';
import { getNotificacoes, marcarMensagemComo } from '../../../api/notificacoes';
import { useMenuSidebar } from '../../../contexts/MenuSidebarContext';
import './styles.scss';

const useAlertSideBarHook = ({ setAlertSideBarOpen, setQtdIconeNotificacao }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [loadReadMsg, setLoadReadMsg] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const { user } = useMenuSidebar();

  useEffect(() => {
    buscaNotificaoes();
  }, []);

  useEffect(() => {
    if (user) {
      initEcho();
      const echo = getEcho();
      const codTipoPerfil = user?.codTipoPerfil;
      const dominio = getConfigAmbienteDominio().replaceAll('.', '_');

      if (!echo || !codTipoPerfil) return;

      echo
        .private(`notificacao.${dominio}.${codTipoPerfil}`)
        .listen('.notificacao.recebida', ({ title, body }) => {
          buscaNotificaoes();
          disparaMensagemDesktop(title, body);
        });

      return () => echo.leave(`notificacao.${codTipoPerfil}`);
    }
  }, [user]);

  // Notificacao para usuario do capital flow
  useEffect(() => {
    if (user) {
      initEcho();
      const echo = getEcho();
      const uuidUsuario = user?.uuid;
      const dominio = getConfigAmbienteDominio().replaceAll('.', '_');

      if (!echo || !uuidUsuario) return;

      echo
        .private(`notificacao_usuario.${dominio}.${uuidUsuario}`)
        .listen('.notificacao_usuario.recebida', ({ title, body }) => {
          buscaNotificaoes();
          disparaMensagemDesktop(title, body);
        });

      return () => echo.leave(`notificacao_usuario.${uuidUsuario}`);
    }
  }, [user]);

  const buscaNotificaoes = () => {
    getNotificacoes()
      .then(({ data }) => {
        setNotifications(data);
      })
      .catch(console.error);
  };

  const markAsRead = useCallback((id, alreadyRead) => {
    setLoadReadMsg(id);
    const valorInvertido = alreadyRead === 'S' ? 'N' : 'S';
    const params = { id: id, read: valorInvertido };
    marcarMensagemComo(params)
      .then(() => {
        setNotifications(prev => prev.map(n => (n.id === id ? { ...n, read: valorInvertido } : n)));
      })
      .finally(() => setLoadReadMsg(false))
      .catch(console.error);
  }, []);

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const qtdUnRead = notifications.filter(n => n.read === 'N').length;
    setQtdIconeNotificacao(qtdUnRead > 0);
  }, [notifications]);

  const toggleDrawer = newOpen => () => {
    setAlertSideBarOpen(newOpen);
  };

  const filteredNotifications = notifications.filter(n => {
    const title = (n.title ?? '').toLowerCase();
    const body = (n.body ?? '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return title.includes(term) || body.includes(term);
  });

  const limitaTamanhoTexto = (text = '', max = 150) => {
    const str = typeof text === 'string' ? text : String(text);
    return str.length > max ? `${str.slice(0, max).trim()}...` : str;
  };

  const disparaMensagemDesktop = (titulo, mensagem) => {
    const faviconUrl = getConfigAmbiente()?.imagens?.FAVICON ?? '';
    showNotification(titulo, {
      body: mensagem,
      icon: faviconUrl
    });
  };

  const formataDataHora = (data = '') => {
    data = data ?? new Date().toISOString();
    return moment(data).format('DD/MM/yyyy [às] HH:mm:ss');
  };

  const markAllAsRead = () => {
    const params = { id: 'all', read: 'S' };
    marcarMensagemComo(params)
      .then(() => {
        buscaNotificaoes();
      })
      .catch(console.error);
  };

  const displayNotifications = showOnlyUnread
    ? filteredNotifications.filter(n => n.read === 'N')
    : filteredNotifications;

  return {
    toggleDrawer,
    notifications,
    filteredNotifications,
    searchTerm,
    setSearchTerm,
    markAsRead,
    limitaTamanhoTexto,
    showOnlyUnread,
    setShowOnlyUnread,
    displayNotifications,
    formataDataHora,
    loadReadMsg,
    openMenu,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
    markAllAsRead
  };
};

export default useAlertSideBarHook;
